import { useEffect, useState } from "react";

import {
  AdminFeedbackDetail,
  AdminInboxRow,
  FeedbackStatus,
  PatchFeedbackIn,
  useAdminClusters,
  useAdminFeedbackDetail,
  useAdminFeedbackHistory,
  useAdminInbox,
  usePatchFeedback,
} from "../../api/feedback";
import StatusBadge, { STATUS_LABEL } from "./StatusBadge";

const STATUS_ORDER: FeedbackStatus[] = [
  "pending",
  "triaged",
  "planned",
  "in_progress",
  "deployed",
  "wont_do",
  "duplicate",
];
const KIND_OPTIONS = ["bug", "feature", "other"];
const RANGE_OPTIONS = [
  { id: 7, label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
  { id: 365, label: "1y" },
];

function fmt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Inbox() {
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus[]>([
    "pending",
    "triaged",
  ]);
  const [kindFilter, setKindFilter] = useState<string[]>([]);
  const [days, setDays] = useState(90);
  const [selected, setSelected] = useState<number | null>(null);

  const inbox = useAdminInbox({
    status: statusFilter,
    kind: kindFilter,
    days,
    limit: 100,
  });

  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_420px]">
      <div>
        {/* Filters */}
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <div className="flex gap-1 rounded-full bg-slate-900 p-0.5 ring-1 ring-slate-800">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.id}
                onClick={() => setDays(r.id)}
                className={`rounded-full px-3 py-1 text-xs transition ${
                  days === r.id
                    ? "bg-slate-800 text-slate-100"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <FilterChips
            label="status"
            options={STATUS_ORDER}
            selected={statusFilter}
            onToggle={(v) =>
              setStatusFilter((prev) =>
                prev.includes(v as FeedbackStatus)
                  ? prev.filter((x) => x !== v)
                  : [...prev, v as FeedbackStatus],
              )
            }
            renderLabel={(v) => STATUS_LABEL[v as FeedbackStatus] ?? v}
          />
          <FilterChips
            label="kind"
            options={KIND_OPTIONS}
            selected={kindFilter}
            onToggle={(v) =>
              setKindFilter((prev) =>
                prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
              )
            }
          />
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-800">
          {inbox.isLoading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : (inbox.data?.rows ?? []).length === 0 ? (
            <div className="p-6 text-sm text-slate-500">
              No items match these filters.
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Kind</th>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Cluster</th>
                  <th className="px-3 py-2 text-right">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 bg-slate-950/40 text-slate-200">
                {inbox.data!.rows.map((r) => (
                  <Row
                    key={r.id}
                    row={r}
                    selected={selected === r.id}
                    onClick={() => setSelected(r.id === selected ? null : r.id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {inbox.data && (
          <div className="mt-2 text-[11px] text-slate-500">
            {inbox.data.rows.length} of {inbox.data.total}
          </div>
        )}
      </div>

      <SidePanel id={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function Row({
  row,
  selected,
  onClick,
}: {
  row: AdminInboxRow;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <tr
      onClick={onClick}
      className={`cursor-pointer transition ${
        selected ? "bg-sky-500/10" : "hover:bg-slate-900/60"
      }`}
    >
      <td className="px-3 py-2 capitalize text-slate-400">{row.kind}</td>
      <td className="max-w-[24ch] truncate px-3 py-2 text-slate-100">{row.title}</td>
      <td className="max-w-[20ch] truncate px-3 py-2 text-slate-400">
        {row.user_email ?? `uid:${row.user_id ?? "?"}`}
      </td>
      <td className="px-3 py-2">
        <StatusBadge status={row.status} />
      </td>
      <td className="max-w-[14ch] truncate px-3 py-2 text-slate-500">
        {row.cluster_title ?? "—"}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">
        {fmt(row.created_at)}
      </td>
    </tr>
  );
}

function FilterChips({
  label,
  options,
  selected,
  onToggle,
  renderLabel,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  renderLabel?: (v: string) => string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const active = selected.includes(o);
          return (
            <button
              key={o}
              onClick={() => onToggle(o)}
              className={`rounded-full px-2 py-0.5 text-[11px] capitalize transition ring-1 ${
                active
                  ? "bg-slate-800 text-slate-100 ring-slate-700"
                  : "text-slate-500 ring-transparent hover:text-slate-300"
              }`}
            >
              {renderLabel ? renderLabel(o) : o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SidePanel({ id, onClose }: { id: number | null; onClose: () => void }) {
  const detail = useAdminFeedbackDetail(id);
  const history = useAdminFeedbackHistory(id);

  if (id == null) {
    return (
      <aside className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/30 p-6 text-sm text-slate-500">
        Select a row to view details.
      </aside>
    );
  }

  if (detail.isLoading || !detail.data) {
    return (
      <aside className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-sm text-slate-500">
        Loading…
      </aside>
    );
  }

  return (
    <aside className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">
            #{detail.data.id} · {detail.data.kind}
          </div>
          <h3 className="mt-0.5 text-base font-semibold text-slate-100">
            {detail.data.title}
          </h3>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {detail.data.user_email ?? `uid:${detail.data.user_id ?? "?"}`} ·{" "}
            {fmt(detail.data.created_at)}
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
        >
          ×
        </button>
      </div>

      <p className="whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-200">
        {detail.data.body}
      </p>

      <div className="space-y-1 text-[11px] text-slate-500">
        {detail.data.page_url && (
          <div>
            <span className="text-slate-400">Page: </span>
            <span className="font-mono">{detail.data.page_url}</span>
          </div>
        )}
        {detail.data.user_agent && (
          <div className="truncate">
            <span className="text-slate-400">UA: </span>
            <span className="font-mono">{detail.data.user_agent}</span>
          </div>
        )}
      </div>

      <Editor detail={detail.data} />

      <div>
        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
          Audit timeline
        </div>
        {history.isLoading ? (
          <div className="text-xs text-slate-500">Loading…</div>
        ) : (history.data ?? []).length === 0 ? (
          <div className="text-xs text-slate-500">No changes yet.</div>
        ) : (
          <ol className="space-y-1.5">
            {history.data!.map((h) => (
              <li key={h.id} className="text-[11px] text-slate-400">
                <span className="font-mono text-[10px] text-slate-500">
                  {fmt(h.created_at)}
                </span>{" "}
                <span>
                  {h.from_status
                    ? `${STATUS_LABEL[h.from_status as FeedbackStatus] ?? h.from_status} → `
                    : ""}
                  <span className="text-slate-200">
                    {STATUS_LABEL[h.to_status as FeedbackStatus] ?? h.to_status}
                  </span>
                </span>{" "}
                <span className="text-slate-500">
                  by {h.by_user_email ?? "?"}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </aside>
  );
}

function Editor({ detail }: { detail: AdminFeedbackDetail }) {
  const patch = usePatchFeedback();
  const clusters = useAdminClusters();
  const [userMsg, setUserMsg] = useState(detail.user_msg ?? "");
  const [adminNote, setAdminNote] = useState(detail.admin_note ?? "");

  // Reset locally-edited fields when switching items.
  useEffect(() => {
    setUserMsg(detail.user_msg ?? "");
    setAdminNote(detail.admin_note ?? "");
  }, [detail.id, detail.user_msg, detail.admin_note]);

  const apply = (body: PatchFeedbackIn) =>
    patch.mutate({ id: detail.id, body });

  const userMsgDirty = userMsg !== (detail.user_msg ?? "");
  const adminNoteDirty = adminNote !== (detail.admin_note ?? "");

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500">
          Status
        </label>
        <select
          value={detail.status}
          onChange={(e) =>
            apply({ status: e.target.value as FeedbackStatus })
          }
          className="w-full rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs text-slate-200"
        >
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500">
          User-visible message
        </label>
        <textarea
          value={userMsg}
          onChange={(e) => setUserMsg(e.target.value)}
          onBlur={() => userMsgDirty && apply({ user_msg: userMsg })}
          rows={3}
          placeholder="Will land in next deploy. Tracking in cluster #4."
          className="w-full resize-y rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-600"
        />
      </div>

      <div>
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500">
          Admin note (private)
        </label>
        <textarea
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          onBlur={() => adminNoteDirty && apply({ admin_note: adminNote })}
          rows={2}
          placeholder="Notes for me, not shown to user."
          className="w-full resize-y rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-600"
        />
      </div>

      <div>
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500">
          Cluster
        </label>
        <select
          value={detail.cluster_id ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") apply({ unassign_cluster: true });
            else apply({ cluster_id: Number(v) });
          }}
          className="w-full rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs text-slate-200"
        >
          <option value="">— unassigned —</option>
          {(clusters.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              #{c.id} · {c.title} ({c.member_count})
            </option>
          ))}
        </select>
      </div>

      {patch.isError && (
        <div className="text-[11px] text-rose-300">
          Couldn&apos;t save: {patch.error.message}
        </div>
      )}
    </div>
  );
}
